/*
 * SchoolsOverviewPage — chain-admin dashboard showing every active
 * branch's KPIs side-by-side. Lets SuperAdmin / SuperDev see the
 * whole network at a glance without context-switching through each
 * branch one at a time.
 *
 * Reads `/api/schools/overview` — one row per active school with:
 *   students · employees · active enrolments
 *   tuition outstanding (₱)
 *   broken lab devices · pending repairs
 *   clinic visits today
 *
 * Premium UX:
 *   - Gold-cream-maroon palette matching the other admin pages.
 *   - Each school is a "branch card" with its own KPI grid + a
 *     CTA to switch context into that school's workspace.
 *   - Hover-lift animation + prefers-reduced-motion opt-out.
 *   - Empty state when only one school exists (the bootstrap one)
 *     pointing the admin at the SchoolsPage to add more.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import API_BASE from "../../config";
import s from "./SchoolsOverviewPage.module.css";

const peso = (n) => {
  const v = Number(n);
  if (!Number.isFinite(v)) return "—";
  return "₱" + v.toLocaleString(undefined,
      { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};
const num = (n) => {
  const v = Number(n);
  if (!Number.isFinite(v)) return "0";
  return v.toLocaleString();
};

function getUser() {
  try { return JSON.parse(sessionStorage.getItem("user") || localStorage.getItem("user") || "null"); }
  catch { return null; }
}

export default function SchoolsOverviewPage() {
  const navigate = useNavigate();
  const user     = getUser();
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState("");

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_BASE}/api/schools/overview`)
      .then(r => r.ok ? r.json() : Promise.reject(new Error(`Load failed (${r.status})`)))
      .then(list => {
        if (cancelled) return;
        setRows(Array.isArray(list) ? list : []);
        setLoading(false);
      })
      .catch(e => { if (!cancelled) { setError(e.message || "Failed to load"); setLoading(false); } });
    return () => { cancelled = true; };
  }, []);

  // Chain-wide totals — sum of every row's KPI. Memoised so it doesn't
  // recompute on every render (the rows array is stable post-fetch).
  const totals = useMemo(() => {
    const sum = { students: 0, employees: 0, enrol: 0, outstanding: 0,
                  broken: 0, pendingRepairs: 0, visitsToday: 0 };
    for (const r of rows) {
      sum.students       += Number(r.students)           || 0;
      sum.employees      += Number(r.employees)          || 0;
      sum.enrol          += Number(r.enrollmentsActive)  || 0;
      sum.outstanding    += Number(r.tuitionOutstanding) || 0;
      sum.broken         += Number(r.labDevicesBroken)   || 0;
      sum.pendingRepairs += Number(r.repairsPending)     || 0;
      sum.visitsToday    += Number(r.visitsToday)        || 0;
    }
    return sum;
  }, [rows]);

  /**
   * Switch the chain-admin's active school to the picked branch and
   * jump to that branch's dashboard. Uses the same sessionStorage +
   * hard-reload pattern as the Topbar's SchoolPill so every cached
   * SPA state rebuilds against the new X-School-Id header.
   */
  const switchInto = (school, target = "/dashboard") => {
    try {
      ["sessionStorage", "localStorage"].forEach(k => {
        const raw = window[k].getItem("user");
        if (!raw) return;
        const obj = JSON.parse(raw);
        obj.school = school;
        window[k].setItem("user", JSON.stringify(obj));
      });
    } catch { /* ignore */ }
    window.location.assign(target);
  };

  return (
    <div className={s.page}>
      <section className={s.hero}>
        <div className={s.heroIcon} aria-hidden>🌐</div>
        <div className={s.heroBody}>
          <div className={s.eyebrow}>Chain overview</div>
          <h1 className={s.heroTitle}>All schools at a glance</h1>
          <p className={s.heroSub}>
            One row per active branch with the headline numbers — students,
            staff, outstanding tuition, broken lab devices, today's clinic
            visits. Tap a card to switch context into that branch's
            workspace, or open Manage Schools to add a new one.
          </p>
        </div>
        <button className={s.btnSecondary} onClick={() => navigate("/schools")}>
          Manage schools →
        </button>
      </section>

      {error && <div className={s.errorBanner}>{error}</div>}
      {loading && <div className={s.loading}>Loading branches…</div>}

      {!loading && !error && rows.length === 0 && (
        <div className={s.emptyState}>
          <div className={s.emptyIcon}>🏫</div>
          <div className={s.emptyTitle}>No active branches yet</div>
          <div className={s.emptyText}>
            The system needs at least one active school. Go to{" "}
            <strong>Manage schools</strong> to create or re-activate one.
          </div>
        </div>
      )}

      {!loading && !error && rows.length > 0 && (
        <>
          {/* ── Chain-wide totals strip ─────────────────────────────
              Tiny KPI band above the per-branch grid so the chain
              admin sees the total network size in one glance before
              diving into per-branch numbers. */}
          <div className={s.chainTotalsCard}>
            <div className={s.chainTotalsHead}>
              <div className={s.chainTotalsEyebrow}>Chain-wide totals · {rows.length} active branch{rows.length === 1 ? "" : "es"}</div>
            </div>
            <div className={s.chainTotalsGrid}>
              <ChainStat label="Students"      value={num(totals.students)} />
              <ChainStat label="Employees"     value={num(totals.employees)} />
              <ChainStat label="Active enrolments" value={num(totals.enrol)} />
              <ChainStat label="Tuition outstanding" value={peso(totals.outstanding)} tone="warn" />
              <ChainStat label="Broken devices" value={num(totals.broken)}      tone={totals.broken > 0 ? "danger" : "ok"} />
              <ChainStat label="Pending repairs" value={num(totals.pendingRepairs)} tone={totals.pendingRepairs > 0 ? "warn" : "ok"} />
              <ChainStat label="Clinic visits today" value={num(totals.visitsToday)} />
            </div>
          </div>

          {/* ── Per-branch cards ──────────────────────────────────── */}
          <div className={s.grid}>
            {rows.map((r) => {
              const sc = r.school || {};
              const activeNow = user?.school?.id === sc.id;
              return (
                <article key={sc.id || sc.code} className={s.branchCard}>
                  <header className={s.branchHead}>
                    {sc.logoUrl
                      ? <img src={sc.logoUrl} alt="" className={s.branchLogo} />
                      : <div className={s.branchLogoDot} aria-hidden>🏫</div>}
                    <div className={s.branchHeadText}>
                      <div className={s.branchCode}>{sc.code}</div>
                      <div className={s.branchName} title={sc.name}>{sc.name}</div>
                      {sc.shortName && (
                        <div className={s.branchShort}>{sc.shortName}</div>
                      )}
                    </div>
                    {activeNow && (
                      <span className={s.branchActivePill} title="Your current context">
                        Current
                      </span>
                    )}
                  </header>

                  <div className={s.branchKpis}>
                    <KPI label="Students"           value={num(r.students)} />
                    <KPI label="Employees"          value={num(r.employees)} />
                    <KPI label="Active enrolments"  value={num(r.enrollmentsActive)} />
                    <KPI label="Tuition outstanding" value={peso(r.tuitionOutstanding)}
                         tone={Number(r.tuitionOutstanding) > 0 ? "warn" : "ok"} />
                    <KPI label="Broken devices"     value={num(r.labDevicesBroken)}
                         tone={Number(r.labDevicesBroken) > 0 ? "danger" : "ok"} />
                    <KPI label="Pending repairs"    value={num(r.repairsPending)}
                         tone={Number(r.repairsPending) > 0 ? "warn" : "ok"} />
                    <KPI label="Clinic visits today" value={num(r.visitsToday)} />
                  </div>

                  <div className={s.branchActions}>
                    <button
                      className={s.btnPrimary}
                      onClick={() => switchInto(sc, "/dashboard")}
                      disabled={activeNow}
                      title={activeNow
                        ? "You're already in this branch's context"
                        : `Switch context into ${sc.shortName || sc.name}`}
                    >
                      {activeNow ? "Currently here" : "Switch into this branch →"}
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

function KPI({ label, value, tone }) {
  return (
    <div className={`${s.kpi} ${tone ? s[`kpi_${tone}`] : ""}`}>
      <div className={s.kpiLabel}>{label}</div>
      <div className={s.kpiValue}>{value}</div>
    </div>
  );
}

function ChainStat({ label, value, tone }) {
  return (
    <div className={`${s.chainStat} ${tone ? s[`chainStat_${tone}`] : ""}`}>
      <div className={s.chainStatLabel}>{label}</div>
      <div className={s.chainStatValue}>{value}</div>
    </div>
  );
}
